import React, { useState } from 'react';
import { Mail, MapPin, Phone, Send, Shield } from 'lucide-react';
import { SEO } from '../components/layout/SEO';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { Card, CardContent } from '../components/ui/Card';
import { FadeIn } from '../components/ui/FadeIn';
export function ContactPage() {
  const [formState, setFormState] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: ''
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Form submission logic would go here
    console.log('Form submitted:', formState);
  };
  return <>
      <SEO title="Contact Us" description="Get in touch with Khyber Medical Association UK. We're here to answer your questions about membership, events, and charitable work." data-id="element-300" />

      <div className="bg-primary-900 text-white py-16 md:py-20" data-id="element-301">
        <div className="container mx-auto px-4 text-center" data-id="element-302">
          <FadeIn data-id="element-303">
            <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4" data-id="element-304">
              Contact Us
            </h1>
            <p className="text-xl text-primary-100 max-w-2xl mx-auto" data-id="element-305">
              We'd love to hear from you. Whether you have questions about
              membership, want to support our charity work, or just want to say
              hello.
            </p>
          </FadeIn>
        </div>
      </div>

      <Section data-id="element-306">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto" data-id="element-307">
          {/* Contact Information */}
          <FadeIn data-id="element-308">
            <div className="space-y-8" data-id="element-309">
              <div data-id="element-310">
                <h2 className="text-2xl font-serif font-bold text-gray-900 mb-6" data-id="element-311">
                  Get in Touch
                </h2>
                <p className="text-gray-600 mb-8 leading-relaxed" data-id="element-312">
                  Whether you have a question about membership, want to donate,
                  volunteer, or just want to learn more about our work, we're
                  here to help.
                </p>
              </div>

              <div className="space-y-6" data-id="element-313">
                <Card className="border-primary-100" data-id="element-314">
                  <CardContent className="flex items-start p-6" data-id="element-315">
                    <div className="flex-shrink-0 h-12 w-12 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center mr-4" data-id="element-316">
                      <MapPin className="h-6 w-6" data-id="element-317" />
                    </div>
                    <div data-id="element-318">
                      <h3 className="font-bold text-gray-900 mb-2" data-id="element-319">
                        Postal Address
                      </h3>
                      <p className="text-gray-600 leading-relaxed" data-id="element-320">
                        17 Honeybourne
                        <br data-id="element-321" />
                        Bishop's Stortford
                        <br data-id="element-322" />
                        CM23 4EF
                        <br data-id="element-323" />
                        United Kingdom
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-primary-100" data-id="element-324">
                  <CardContent className="flex items-center p-6" data-id="element-325">
                    <div className="flex-shrink-0 h-12 w-12 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center mr-4" data-id="element-326">
                      <Mail className="h-6 w-6" data-id="element-327" />
                    </div>
                    <div data-id="element-328">
                      <h3 className="font-bold text-gray-900 mb-2" data-id="element-329">
                        Email Address
                      </h3>
                      <a href="mailto:info@kmauk.org" className="text-primary-700 hover:underline text-lg font-medium" data-id="element-330">
                        info@kmauk.org
                      </a>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Reassurance */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-6" data-id="element-331">
                <div className="flex items-start gap-3" data-id="element-332">
                  <Shield className="h-5 w-5 text-green-600 shrink-0 mt-0.5" data-id="element-333" />
                  <div data-id="element-334">
                    <h4 className="font-bold text-gray-900 mb-2" data-id="element-335">
                      Your Privacy Matters
                    </h4>
                    <p className="text-sm text-gray-700 leading-relaxed" data-id="element-336">
                      We respect your privacy and will never share your
                      information with third parties. All communications are
                      handled securely and confidentially.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>

          {/* Contact Form */}
          <FadeIn delay={0.2} data-id="element-337">
            <Card className="border-primary-100 shadow-lg" data-id="element-338">
              <CardContent className="p-8" data-id="element-339">
                <h3 className="text-xl font-bold text-gray-900 mb-6" data-id="element-340">
                  Send us a Message
                </h3>
                <form className="space-y-5" onSubmit={handleSubmit} data-id="element-341">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-342">
                    <Input label="First Name" placeholder="John" value={formState.firstName} onChange={e => setFormState({
                    ...formState,
                    firstName: e.target.value
                  })} required data-id="element-343" />
                    <Input label="Last Name" placeholder="Doe" value={formState.lastName} onChange={e => setFormState({
                    ...formState,
                    lastName: e.target.value
                  })} required data-id="element-344" />
                  </div>
                  <Input label="Email Address" type="email" placeholder="john.doe@example.com" value={formState.email} onChange={e => setFormState({
                  ...formState,
                  email: e.target.value
                })} required data-id="element-345" />
                  <Input label="Subject" placeholder="How can we help you?" value={formState.subject} onChange={e => setFormState({
                  ...formState,
                  subject: e.target.value
                })} required data-id="element-346" />
                  <Textarea label="Message" placeholder="Tell us more about your inquiry..." rows={6} value={formState.message} onChange={e => setFormState({
                  ...formState,
                  message: e.target.value
                })} required data-id="element-347" />
                  <Button type="submit" className="w-full" size="lg" data-id="element-348">
                    <Send className="mr-2 h-5 w-5" data-id="element-349" />
                    Send Message
                  </Button>
                  <p className="text-xs text-gray-500 text-center" data-id="element-350">
                    We typically respond within 48 hours during business days.
                  </p>
                </form>
              </CardContent>
            </Card>
          </FadeIn>
        </div>
      </Section>

      {/* Quick Links */}
      <Section background="gray" className="text-center" data-id="element-351">
        <FadeIn data-id="element-352">
          <h2 className="text-2xl font-serif font-bold text-gray-900 mb-8" data-id="element-353">
            Looking for Something Specific?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto" data-id="element-354">
            <Card className="hover:shadow-md transition-shadow" data-id="element-355">
              <CardContent className="p-6 text-center" data-id="element-356">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-357">
                  Membership Inquiries
                </h3>
                <p className="text-sm text-gray-600 mb-4" data-id="element-358">
                  Questions about joining KMA UK?
                </p>
                <a href="/membership" className="text-primary-700 hover:underline text-sm font-medium" data-id="element-359">
                  View Membership Options →
                </a>
              </CardContent>
            </Card>

            <Card className="hover:shadow-md transition-shadow" data-id="element-360">
              <CardContent className="p-6 text-center" data-id="element-361">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-362">
                  Charitable Donations
                </h3>
                <p className="text-sm text-gray-600 mb-4" data-id="element-363">
                  Want to support our projects?
                </p>
                <a href="/charity" className="text-primary-700 hover:underline text-sm font-medium" data-id="element-364">
                  Learn About Our Work →
                </a>
              </CardContent>
            </Card>

            <Card className="hover:shadow-md transition-shadow" data-id="element-365">
              <CardContent className="p-6 text-center" data-id="element-366">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-367">
                  Event Information
                </h3>
                <p className="text-sm text-gray-600 mb-4" data-id="element-368">
                  Interested in upcoming events?
                </p>
                <a href="/events" className="text-primary-700 hover:underline text-sm font-medium" data-id="element-369">
                  See Event Calendar →
                </a>
              </CardContent>
            </Card>
          </div>
        </FadeIn>
      </Section>
    </>;
}